import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkle, Broadcast, CalendarBlank, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast, Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import promotionImg from "../../../Assets/megaphone (1).png";
import axiosInstance from "../../../api/axiosInstance";
import NotificationProvider from "../../NotificationProvider";
import PageLoader from "../../loader/PageLoader";
import PromotionCard from "./PromotionCard";
import PromotionsModal from "./PromotionsModal";
import PromotionViewModal from "./PromotionViewModal";
import { PROMO_TABS, PROMO_STATUS, formatINRWhole } from "./promoUtils";

const TAB_ICONS = {
    active: Broadcast,
    ai: Sparkle,
    scheduled: CalendarBlank,
    past: ClockCounterClockwise,
};

export default function Promotions() {
    const hotelId = localStorage.getItem("hotelId");
    const queryClient = useQueryClient();

    const [activeTab, setActiveTab] = useState("active");
    const [createOpen, setCreateOpen] = useState(false);
    const [viewPromo, setViewPromo] = useState(null);

    // ---- Fetch all promotions once, group by status client-side ----
    const fetchPromotions = async () => {
        const response = await axiosInstance.get(`/api/v1/hotels/${hotelId}/promotions`);
        return Array.isArray(response.data) ? response.data : [];
    };

    const { data: promotions = [], isLoading } = useQuery({
        queryKey: ["promotions", hotelId],
        queryFn: fetchPromotions,
        refetchOnWindowFocus: true,
        staleTime: 5000,
    });

    const grouped = useMemo(() => {
        const buckets = { ACTIVE: [], SUGGESTED: [], SCHEDULED: [], ENDED: [] };
        promotions.forEach((p) => {
            if (buckets[p.status]) buckets[p.status].push(p);
        });
        return buckets;
    }, [promotions]);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["promotions", hotelId] });
    };

    // ---- Mutations ----
    // Approve an AI suggestion: go live today, run for 2 years, let Alfred decide the audience.
    const approvePromo = async (promo) => {
        try {
            const now = dayjs();
            await axiosInstance.put(`/api/v1/hotels/${hotelId}/promotions/${promo.id}`, {
                status: PROMO_STATUS.SCHEDULED,
                target_audience: "ALFRED",
                start_date: now.utcOffset(330).format(),
                end_date: now.add(2, "year").utcOffset(330).format(),
            });
            toast.success("Promotion approved and live");
            invalidate();
        } catch (error) {
            toast.error(`Could not approve: ${error?.response?.data?.error || "Unknown error"}`);
        }
    };

    const discardPromo = async (promo) => {
        try {
            await axiosInstance.post(`/api/v1/hotels/${hotelId}/promotions/${promo.id}/discard`);
            toast.success("Suggestion discarded");
            invalidate();
        } catch (error) {
            toast.error(`Could not discard: ${error?.response?.data?.error || "Unknown error"}`);
        }
    };

    const stopPromo = async (promo) => {
        try {
            await axiosInstance.post(`/api/v1/hotels/${hotelId}/promotions/${promo.id}/stop`);
            toast.success("Promotion stopped");
            invalidate();
        } catch (error) {
            toast.error(`Could not stop: ${error?.response?.data?.error || "Unknown error"}`);
        }
    };

    const currentStatus = PROMO_TABS.find((t) => t.id === activeTab)?.status;
    const cards = grouped[currentStatus] || [];

    return (
        <>
            <NotificationProvider />
            {isLoading && <PageLoader />}

            <div className="w-full">
                {/* Header */}
                <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                    <h2 className="text-xl font-semibold tracking-[4px]">PROMOTIONS</h2>
                    <button
                        className="flex items-center gap-2 bg-[#ff7378] px-4 py-2 text-white rounded-lg shadow-sm text-sm cursor-pointer hover:bg-[#f75f64] transition"
                        onClick={() => setCreateOpen(true)}
                    >
                        <img src={promotionImg} className="w-5 h-5" alt="" /> Create Promotion
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
                    {PROMO_TABS.map((t) => {
                        const count = (grouped[t.status] || []).length;
                        const isActive = t.id === activeTab;
                        const Icon = TAB_ICONS[t.id];
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                className={`relative flex items-center gap-2 px-4 py-3 text-[13.5px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors duration-200 ${
                                    isActive
                                        ? "text-[#ff7378] border-[#ff7378]"
                                        : "text-gray-400 border-transparent hover:text-gray-600"
                                }`}
                            >
                                <Icon size={16} weight={isActive ? "fill" : "regular"} />
                                {t.label}
                                <span
                                    className={`min-w-[20px] text-center text-[11px] font-bold px-1.5 py-0.5 rounded-full transition-colors ${
                                        isActive ? "bg-[#FFF0F0] text-[#ff7378]" : "bg-gray-100 text-gray-400"
                                    }`}
                                >
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Running-now banner */}
                {activeTab === "active" && cards.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-5 px-4 py-2.5 bg-[#F0FDF4] border border-[#bbf7d0] rounded-lg">
                        <span className="text-sm font-semibold text-[#16a34a]">
                            {cards.length} promotion{cards.length !== 1 ? "s" : ""} currently running
                        </span>
                        <span className="text-gray-300">·</span>
                        <span className="text-sm text-gray-500">Total upsell generated:</span>
                        <span className="text-[15px] font-bold text-[#16a34a]">
                            {formatINRWhole(cards.reduce((sum, p) => sum + (p.upsell_generated || 0), 0))}
                        </span>
                    </div>
                )}

                {/* Card grid */}
                {!isLoading &&
                    (cards.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {cards.map((promo) => (
                                <PromotionCard
                                    key={promo.id}
                                    promo={promo}
                                    onView={setViewPromo}
                                    onApprove={approvePromo}
                                    onDiscard={discardPromo}
                                    onStop={stopPromo}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 text-gray-400 text-sm">
                            {activeTab === "ai"
                                ? "No AI suggestions yet."
                                : "No promotions here yet."}
                        </div>
                    ))}
            </div>

            {createOpen && (
                <PromotionsModal onClose={() => setCreateOpen(false)} onCreated={invalidate} />
            )}
            {viewPromo && (
                <PromotionViewModal promo={viewPromo} onClose={() => setViewPromo(null)} />
            )}

            <Toaster position="bottom-right" reverseOrder={false} style={{ marginBottom: "25%" }} />
        </>
    );
}
